import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Building2, 
  Upload, 
  Save, 
  Loader2, 
  Globe, 
  Phone, 
  Mail, 
  MapPin,
  Camera,
  Trash2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const OrganizationSettingsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [orgData, setOrgData] = useState({
    company_name: '',
    subdomain: '',
    description: '',
    website: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    country: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchOrganizationProfile();
  }, []);

  const fetchOrganizationProfile = async () => {
    try {
      const response = await api().get('/organization/profile');
      setOrgData(response.data);
    } catch (error) {
      console.error('Failed to fetch organization profile:', error);
      // Use tenant data as fallback
      if (user?.tenant) {
        setOrgData(prev => ({
          ...prev,
          company_name: user.tenant.company_name || '',
          subdomain: user.tenant.subdomain || ''
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().put('/organization/profile', orgData);
      toast.success('Organization profile updated successfully');
    } catch (error) {
      toast.error('Failed to update organization profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await api().post('/organization/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setOrgData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
      // Refresh page to update sidebar
      window.location.reload();
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api().delete('/organization/logo');
      setOrgData(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo removed');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'CO';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="organization-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Organization Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your organization's profile and branding</p>
      </div>

      {/* Logo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Organization Logo
          </CardTitle>
          <CardDescription>
            Upload your organization's logo. It will be displayed in the sidebar and throughout the application.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="relative group">
              <Avatar className="w-24 h-24 border-4 border-primary/20">
                {orgData.logo_url ? (
                  <AvatarImage src={orgData.logo_url} alt={orgData.company_name} />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-amber-500 to-amber-500 text-white text-2xl font-bold">
                  {getInitials(orgData.company_name)}
                </AvatarFallback>
              </Avatar>
              {orgData.logo_url && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={handleRemoveLogo}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Upload Controls */}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Recommended: Square image, at least 200x200px. Max 2MB.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Basic Information
          </CardTitle>
          <CardDescription>
            Your organization's core details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Organization Name</Label>
              <Input
                id="company_name"
                value={orgData.company_name}
                onChange={(e) => setOrgData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Your Company Name"
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subdomain">Workspace URL</Label>
              <div className="flex items-center">
                <Input
                  id="subdomain"
                  value={orgData.subdomain}
                  disabled
                  className="rounded-r-none bg-muted"
                />
                <span className="px-3 py-2 bg-muted border border-l-0 border-input rounded-r-md text-sm text-muted-foreground">
                  .edubridgenetwork.com
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Workspace URL cannot be changed</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={orgData.description || ''}
              onChange={(e) => setOrgData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of your organization..."
              rows={3}
              data-testid="org-description-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Contact Information
          </CardTitle>
          <CardDescription>
            How clients and partners can reach you
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={orgData.email || ''}
                  onChange={(e) => setOrgData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="contact@yourcompany.com"
                  className="pl-10"
                  data-testid="org-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  value={orgData.phone || ''}
                  onChange={(e) => setOrgData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                  className="pl-10"
                  data-testid="org-phone-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="website"
                value={orgData.website || ''}
                onChange={(e) => setOrgData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://www.yourcompany.com"
                className="pl-10"
                data-testid="org-website-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Address
          </CardTitle>
          <CardDescription>
            Your organization's physical location
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address">Street Address</Label>
            <Input
              id="address"
              value={orgData.address || ''}
              onChange={(e) => setOrgData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 Business Street, Suite 100"
              data-testid="org-address-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={orgData.city || ''}
                onChange={(e) => setOrgData(prev => ({ ...prev, city: e.target.value }))}
                placeholder="New York"
                data-testid="org-city-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={orgData.country || ''}
                onChange={(e) => setOrgData(prev => ({ ...prev, country: e.target.value }))}
                placeholder="United States"
                data-testid="org-country-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="min-w-[150px]"
          data-testid="save-org-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationSettingsPage;
